/**
 * Metrics Explore — shared builder types + pure helpers.
 *
 * The Explore surface (MetricsView) is a *single-metric* chart builder: pick one
 * metric from the catalog, optionally split it by a label (one series per value)
 * and/or narrow it with label filters, choose an aggregation + viz, and render.
 * A saved "exploration" serializes exactly this builder state (the query
 * contract §2.3 body + viz/units/scale/colors/split — amendments §3), the metric
 * analogue of a saved filter set.
 *
 * Everything here is dependency-free + pure so it unit-tests without mounting a
 * component and is safe to call from a render path.
 */

import type { CatalogMetric, MetricAgg, MetricMatcher, MetricRangeQuery } from '../api.js';
import type { MetricUnit } from './format-metric.js';
import type { VizType } from '../ui/VizTypePicker.vue';

/** Reserved series-label keys the backend injects (§2.1). Never shown as-is. */
export const RESERVED_TARGET_LABEL = '__target__';
export const RESERVED_SOURCE_LABEL = '__source__';
export const RESERVED_NAME_LABEL = '__name__';

/** Histogram bucket boundary — must never be a groupBy dimension (task note). */
export const HISTOGRAM_BUCKET_LABEL = 'le';

/** A label key is user-facing iff it is not a reserved `__`-prefixed dimension. */
export function isDisplayLabel(key: string): boolean {
  return !key.startsWith('__');
}

/** Strip reserved `__`-prefixed keys from a label set for user-facing display. */
export function displayLabels(labels: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(labels)) {
    if (isDisplayLabel(k)) out[k] = v;
  }
  return out;
}

/**
 * A stable, human name for one result series, built from its label VALUES so no
 * two similarly-named series are ever ambiguous (spec §4.2.4). Reserved `__`
 * keys are excluded EXCEPT the active split key — splitting by `__target__` is
 * allowed (task note), and its value (`api`, `traefik`, …) is the whole point of
 * that split, so it must show. Falls back to the metric name when there are no
 * distinguishing labels (the single-series, no-split case).
 */
export function seriesName(
  labels: Record<string, string>,
  fallback: string,
  splitBy?: string | null,
): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(labels).sort((a, b) => a[0].localeCompare(b[0]))) {
    if (isDisplayLabel(k)) parts.push(v);
  }
  // A reserved split dimension (e.g. __target__) still contributes its value.
  if (splitBy && !isDisplayLabel(splitBy) && labels[splitBy] !== undefined) {
    if (!parts.includes(labels[splitBy])) parts.unshift(labels[splitBy]);
  }
  return parts.length ? parts.join(' · ') : fallback;
}

/** Friendly label for a split/filter dimension (`__target__` → "target"). */
export function labelDisplayName(key: string): string {
  if (key === RESERVED_TARGET_LABEL) return 'target';
  if (key === RESERVED_SOURCE_LABEL) return 'source';
  return key;
}

function titleCase(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/**
 * The "family" a metric belongs to WITHIN its catalog group (spec §4.2.1 — the
 * heart of making a long metric list legible). A group is already scoped to one
 * (source, target); the family is a second level under it, so its label never
 * repeats the target. e.g. within `container`: CPU / Memory / Network / Disk;
 * within `prometheus · api`: Process / Node.js runtime / Event loop / Custom.
 *
 * `order` sorts families into a conventional reading order; it only needs to be
 * consistent within a single group (families never mix across groups).
 */
export function metricFamily(m: { source: string; name: string }): {
  key: string;
  label: string;
  order: number;
} {
  const name = m.name;

  // Dot-named synthetic metrics (container/host): family = the segment after the
  // source prefix, e.g. `container.net.rx.bytes` → `net` → "Network".
  if (name.includes('.')) {
    const segs = name.split('.');
    const root = (segs[0] === m.source ? segs[1] : segs[0]) ?? name;
    const dotMap: Record<string, [string, number]> = {
      cpu: ['CPU', 0],
      mem: ['Memory', 1],
      memory: ['Memory', 1],
      net: ['Network', 2],
      network: ['Network', 2],
      disk: ['Disk', 3],
      fs: ['Filesystem', 3],
      blkio: ['Block I/O', 4],
      io: ['I/O', 4],
      load: ['Load', 5],
      uptime: ['Uptime', 6],
    };
    const hit = dotMap[root];
    return hit
      ? { key: root, label: hit[0], order: hit[1] }
      : { key: root, label: titleCase(root), order: 9 };
  }

  // Underscore-named Prometheus metrics: match a known prefix, longest/most
  // specific first. Labels are group-relative (no "Traefik ·" — the group head
  // already says traefik).
  const promFamilies: Array<[string, string, number]> = [
    ['scrolldex_', 'Custom (scrolldex)', 20],
    ['traefik_service_', 'Services', 1],
    ['traefik_entrypoint_', 'Entrypoints', 2],
    ['traefik_router_', 'Routers', 3],
    ['traefik_config_', 'Config', 4],
    ['traefik_tls_', 'TLS', 5],
    ['traefik_open_conns', 'Connections', 6],
    ['nodejs_gc_', 'Garbage collection', 12],
    ['nodejs_eventloop_', 'Event loop', 13],
    ['nodejs_active_', 'Handles & requests', 14],
    ['nodejs_heap_', 'Memory', 11],
    ['nodejs_external_memory', 'Memory', 11],
    ['nodejs_', 'Node.js runtime', 15],
    ['process_', 'Process', 10],
    ['http_', 'HTTP', 7],
    ['go_', 'Go runtime', 16],
  ];
  for (const [prefix, label, order] of promFamilies) {
    if (name.startsWith(prefix)) return { key: prefix, label, order };
  }
  const seg = name.split('_')[0] || name;
  return { key: seg, label: titleCase(seg), order: 30 };
}

/**
 * The dimensions a metric can be split-by / filtered-by, friendliest first:
 * `target` (the scrape source — always meaningful) then the metric's own label
 * keys. `le` (histogram buckets) is never offered as a split (task note).
 */
export function splittableLabels(metric: CatalogMetric): string[] {
  const keys = metric.labelKeys.filter((k) => k !== HISTOGRAM_BUCKET_LABEL);
  return [RESERVED_TARGET_LABEL, ...keys];
}

/**
 * Sensible aggregation inferred from the metric TYPE so the user never picks
 * `rate()` (spec §4.3.5): counters accumulate → per-second rate; histograms →
 * p95 latency; everything else (gauges) → average over the bucket.
 */
export function defaultAgg(type: string | null | undefined): MetricAgg {
  switch (type) {
    case 'counter':
      return 'rate';
    case 'histogram':
    case 'summary':
      return 'p95';
    default:
      return 'avg';
  }
}

/** Aggregations offered in the advanced menu, ordered per metric type. */
export function aggOptions(type: string | null | undefined): MetricAgg[] {
  if (type === 'counter') return ['rate', 'sum', 'count', 'max', 'avg', 'min', 'last'];
  if (type === 'histogram' || type === 'summary')
    return ['p95', 'p99', 'p90', 'p50', 'avg', 'max', 'min'];
  return ['avg', 'last', 'max', 'min', 'sum', 'count'];
}

/**
 * Map the backend's free-form unit string to a `MetricUnit` formatter bucket.
 * Best-effort — an unknown unit falls back to `none` (compact number) and the
 * user can override in the advanced options.
 */
export function unitFor(metric: Pick<CatalogMetric, 'unit' | 'name'>): MetricUnit {
  const u = (metric.unit ?? '').toLowerCase();
  if (u === 'bytes' || u === 'byte') return 'bytes';
  if (u === 'percent' || u === 'percentage' || u === '%') return 'percent';
  if (u === 'ms' || u === 'milliseconds') return 'ms';
  if (u === 'ratio') return 'ratio';
  if (u === 'count' || u === 'total') return 'count';
  // Infer from the metric name when the unit is absent/opaque.
  const n = metric.name.toLowerCase();
  if (n.endsWith('bytes') || n.endsWith('.bytes')) return 'bytes';
  if (n.includes('percent')) return 'percent';
  return 'none';
}

/** Viz types offered for a metric. Pie is excluded (LgChart can't render it). */
export function vizOptions(): VizType[] {
  return ['line', 'bar', 'area', 'points', 'counter', 'gauge'];
}

/** Friendly default viz for a metric type: counters/gauges look best as lines by
 * default too (the trend matters), but a single-value counter viz is available. */
export function defaultViz(): VizType {
  return 'line';
}

/**
 * A saved exploration's serialized state (amendments §3 `spec_json`). Carries the
 * §2.3 query identity (minus the live from/to/step) PLUS the render config, and
 * enough catalog metadata (`unit`/`type`/`description`/`labelKeys`) to rebuild
 * the catalog chip + advanced defaults without a catalog round-trip.
 */
export interface ExplorationSpec {
  /** Metric identity (§2.3 body). */
  source?: string;
  name: string;
  target?: string;
  matchers?: MetricMatcher[];
  agg: MetricAgg;
  /** Split dimension → `groupBy: [splitBy]` at query time. `null` = no split. */
  splitBy?: string | null;
  /** Render config. */
  viz: VizType;
  yUnit: MetricUnit;
  yScale: 'linear' | 'log';
  /** Per-series display overrides, keyed by series name. */
  aliases?: Record<string, string>;
  colors?: Record<string, string>;
  /** Catalog metadata for chip/label rebuild (display only, not queried). */
  unit?: string | null;
  type?: string | null;
  description?: string;
  labelKeys?: string[];
}

/** Live builder state (the reactive shape MetricsView holds). Separated from the
 * serialized spec so the view owns time-range/live which are NOT saved. */
export interface BuilderState {
  metric: CatalogMetric;
  splitBy: string | null;
  filters: MetricMatcher[];
  agg: MetricAgg;
  viz: VizType;
  yUnit: MetricUnit;
  yScale: 'linear' | 'log';
  aliases: Record<string, string>;
  colors: Record<string, string>;
}

/** Seed a fresh builder from a picked catalog metric with inferred defaults. */
export function builderFromMetric(metric: CatalogMetric): BuilderState {
  return {
    metric,
    splitBy: null,
    filters: [],
    agg: defaultAgg(metric.type),
    viz: defaultViz(),
    yUnit: unitFor(metric),
    yScale: 'linear',
    aliases: {},
    colors: {},
  };
}

/** Serialize the live builder to a saveable spec (drops the full catalog row). */
export function specFromBuilder(b: BuilderState): ExplorationSpec {
  return {
    source: b.metric.source,
    name: b.metric.name,
    target: b.metric.target,
    matchers: b.filters.length ? b.filters : undefined,
    agg: b.agg,
    splitBy: b.splitBy,
    viz: b.viz,
    yUnit: b.yUnit,
    yScale: b.yScale,
    aliases: Object.keys(b.aliases).length ? b.aliases : undefined,
    colors: Object.keys(b.colors).length ? b.colors : undefined,
    unit: b.metric.unit,
    type: b.metric.type,
    description: b.metric.description,
    labelKeys: b.metric.labelKeys,
  };
}

/** Rebuild a live builder from a saved spec (reconstructs the catalog chip). */
export function builderFromSpec(spec: ExplorationSpec): BuilderState {
  const metric: CatalogMetric = {
    name: spec.name,
    source: spec.source ?? '',
    target: spec.target ?? '',
    unit: spec.unit ?? null,
    type: spec.type ?? null,
    labelKeys: spec.labelKeys ?? [],
    description: spec.description,
    lastValue: null,
    sparkline: null,
    deltaPct: null,
  };
  return {
    metric,
    splitBy: spec.splitBy ?? null,
    filters: spec.matchers ? [...spec.matchers] : [],
    agg: spec.agg,
    viz: spec.viz,
    yUnit: spec.yUnit,
    yScale: spec.yScale,
    aliases: spec.aliases ? { ...spec.aliases } : {},
    colors: spec.colors ? { ...spec.colors } : {},
  };
}

/**
 * A reasonable downsample step (ms) for a range: aim for ≤ ~500 buckets so the
 * chart stays legible + the backend scan stays bounded (§2.5), but never finer
 * than the 15s scrape cadence (§5) — sub-scrape steps just yield empty buckets.
 */
export const SCRAPE_INTERVAL_MS = 15_000;
export function stepFor(rangeMs: number): number {
  return Math.max(SCRAPE_INTERVAL_MS, Math.round(rangeMs / 500 / 1000) * 1000);
}

/** Build the §2.3 range-query body from the live builder + a concrete window. */
export function buildRangeQuery(b: BuilderState, from: number, to: number): MetricRangeQuery {
  return {
    source: b.metric.source || undefined,
    name: b.metric.name,
    target: b.metric.target || undefined,
    matchers: b.filters.length ? b.filters : undefined,
    from,
    to,
    step: stepFor(to - from),
    agg: b.agg,
    groupBy: b.splitBy ? [b.splitBy] : undefined,
  };
}
